import { withZod } from '@remix-validated-form/with-zod'
import { z } from 'zod'
import { getI18n } from 'react-i18next'
import { PhoneNumberUtil } from 'google-libphonenumber'

import { VerificationStepsEnum } from '~/components/account-verification/AccountVerification.config'
import { usaZipCodesByState } from '~/helpers/usa-states'
import { isValidTLD } from '~/helpers/tlds'
import * as IBAN from 'iban'
import type { HumanVerificationService } from '~/types/shared'

const PhoneNumberInstance = PhoneNumberUtil.getInstance()
const isValidPhoneNumber = (input: unknown) => {
  if (!input || typeof input !== 'string') return false
  if (input.length === 0) return false
  try {
    return PhoneNumberInstance.isValidNumber(PhoneNumberInstance.parse(input))
  } catch {
    return false
  }
}

class Validation {
  //private static PhoneNumberRegEx: RegExp = /^\+?[1-9][0-9]{7,14}$/ // use isValidPhoneNumber instead
  private static LatinsCharsRegEx: RegExp = /^[A-Za-z ]*$/
  private static LatinsCharsAndNumbersRegEx = /^[A-Za-z0-9 ]*$/
  private static LatinsCharsNumbersCommasRegEx = /^[A-Za-z0-9,' ]*$/
  private static LatinsCharsNumbersSpecialCharsRegEx = /^[A-Za-z0-9!@#$%-=^&*()_+{},.\[\]\\:"|¨;΄'<>?,.\/`~§±' ]*$/

  private static fieldRequired(): string {
    return getI18n().t('field_required', { ns: 'ui' })
  }

  private static fieldLatinOnly(): string {
    return getI18n().t('field_latin_characters', { ns: 'ui' })
  }

  private static fieldLatinAndNumbersOnly(): string {
    return getI18n().t('field_latin_numbers', { ns: 'ui' })
  }

  private static fieldLatinNumbersCommasRegEx(): string {
    return getI18n().t('field_latin_numbers_comma_apostrophe', { ns: 'ui' })
  }

  //TODO: create label on Statamic
  private static fieldLatinNumbersSpecialCharsRegEx(): string {
    return 'Field can only contain latin characters, numbers, and special characters.'
  }

  //TODO: create label on Statamic
  private static datefieldGreaterThan18(): string {
    return 'The age must be greater or equal to 18.'
  }

  private static validEmail(): string {
    return getI18n().t('field_email', { ns: 'ui' })
  }

  private static recaptchaRequired(): string {
    return getI18n().t('recaptcha_required', { ns: 'ui' })
  }

  private static turnstileRequired(): string {
    return getI18n().t('cloudflare_required', { ns: 'ui', defaultValue: "Verify you're not a robot." })
  }

  private static invalidPhone(): string {
    return getI18n().t('invalid_phone', { ns: 'ui' })
  }

  private static invalidPostCode(): string {
    return getI18n().t('invalid_post_code', { ns: 'ui' })
  }

  private static noFileUploaded(): string {
    return 'File required'
    // return getI18n().t('file_required', { ns: 'ui' })
  }

  private static calculateAge = (dateOfBirth: Date): number => {
    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }

    return age
  }

  public static signUp(humanVerificationService: HumanVerificationService, includeCfdDisclaimer = false) {
    let rules: { [key: string]: any } = {
      title: z.string().min(1, { message: this.fieldRequired() }),
      firstName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }), // Rejects empty or whitespace strings
      lastName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }), // Rejects empty or whitespace strings
      email: z.string().min(1, { message: this.fieldRequired() }).email(this.validEmail()),
      country: z.string({ required_error: this.fieldRequired() }),
      phoneNumber: z.string().optional(),
      includePhoneNumber: z.string().optional(),
      recaptcha: z.string().min(1, { message: this.recaptchaRequired() }),
      turnstile: z.string().min(1, { message: this.turnstileRequired() }),
      acceptTerms: z.string({ required_error: this.fieldRequired() }),
      acknowledgePersonalData: z.string({ required_error: this.fieldRequired() }),
      subscribeNewsletter: z.string({ required_error: this.fieldRequired() }),
      cfdDisclaimer: z.string({ required_error: this.fieldRequired() }),
    }

    if (humanVerificationService !== 'GOOGLE_RECAPTCHA') delete rules['recaptcha']
    if (humanVerificationService !== 'CLOUDFLARE_TURNSTILE') delete rules['turnstile']
    if (!includeCfdDisclaimer) delete rules['cfdDisclaimer']

    let zObject = z.object(rules).superRefine((data, ctx) => {
      // Email TLD validation
      const split = data.email.split('.')

      if (split.length > 1 && !isValidTLD(split[split.length - 1])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['email'],
          message: this.validEmail(),
        })
      }

      if (data.includePhoneNumber) {
        if (!isValidPhoneNumber(data.phoneNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['phoneNumber'],
            message: this.invalidPhone(),
          })
        }
      } else {
        data.phoneNumber = undefined
      }
    })

    return withZod(zObject)
  }

  public static guestRegistration(humanVerificationService: HumanVerificationService, includeCfdDisclaimer = false) {
    let rules: { [key: string]: any } = {
      title: z.string().min(1, { message: this.fieldRequired() }),
      firstName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }), // Rejects empty or whitespace strings
      lastName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }), // Rejects empty or whitespace strings
      email: z.string().min(1, { message: this.fieldRequired() }).email(this.validEmail()),
      address: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsNumbersCommasRegEx, this.fieldLatinNumbersCommasRegEx())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      country: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      postCode: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      city: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      state: z.string().regex(this.LatinsCharsRegEx, this.fieldLatinOnly()).optional(),
      phoneNumber: z.string().refine(isValidPhoneNumber, this.invalidPhone()),
      // includePhoneNumber: z.string().optional(),
      recaptcha: z.string().min(1, { message: this.recaptchaRequired() }),
      turnstile: z.string().min(1, { message: this.turnstileRequired() }),
      acceptTerms: z.string({ required_error: this.fieldRequired() }),
      acknowledgePersonalData: z.string({ required_error: this.fieldRequired() }),
      subscribeNewsletter: z.string({ required_error: this.fieldRequired() }),
      cfdDisclaimer: z.string({ required_error: this.fieldRequired() }),
    }

    if (humanVerificationService !== 'GOOGLE_RECAPTCHA') delete rules['recaptcha']
    if (humanVerificationService !== 'CLOUDFLARE_TURNSTILE') delete rules['turnstile']
    if (!includeCfdDisclaimer) delete rules['cfdDisclaimer']

    let zObject = z.object(rules).superRefine((data, ctx) => {
      // Email TLD validation
      const split = data.email.split('.')

      if (split.length > 1 && !isValidTLD(split[split.length - 1])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['email'],
          message: this.validEmail(),
        })
      }

      // Require state only for US and CA
      if (['US', 'CA'].includes(data.country) && (!data.state || data.state.trim().length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['state'],
          message: this.fieldRequired(),
        })
      }

      // Conditional validation for post code for usa states
      const usaPostCodes = usaZipCodesByState

      if (usaPostCodes[data.state] !== undefined && !usaPostCodes[data.state].includes(data.postCode)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['postCode'], message: this.invalidPostCode() })
      }
    })

    return withZod(zObject)
  }

  public static affiliatePersonalInfoStep() {
    let rules = {
      firstName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      lastName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      email: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .email(this.validEmail())
        .refine((val) => !val.includes('+'), { message: this.validEmail() }),
      countryCode: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      languageId: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      phoneNumber: z.string().refine(isValidPhoneNumber, this.invalidPhone()),
      whatsappNumber: z
        .string()
        .optional()
        .refine((val) => (!val ? true : isValidPhoneNumber(val)), this.invalidPhone()),
      password: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .min(6, { message: 'Password must be at least 6 characters' })
        .regex(/.*[0-9].*/, 'Password must contain at least one number'),
      passwordConfirmation: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .min(6, { message: 'Password must be at least 6 characters' })
        .regex(/.*[0-9].*/, 'Password must contain at least one number'),
    }

    let zObject = z.object(rules).superRefine((data, ctx) => {
      const split = data.email.split('.')
      if (split.length > 1 && !isValidTLD(split[split.length - 1])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['email'],
          message: this.validEmail(),
        })
      }

      if (!data.whatsappNumber) {
        data.whatsappNumber = undefined
      }

      if (data.password !== data.passwordConfirmation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['passwordConfirmation'],
          message: 'Passwords do not match',
        })
      }
    })

    return withZod(zObject)
  }

  public static affiliateBusinessDetailsStep() {
    let rules = {
      type: z.string().min(1, { message: this.fieldRequired() }),
      company: z.string().optional(),
      companyDomainURL: z.string().optional(),
      mainFocus: z.string().min(1, { message: this.fieldRequired() }),
      mainFocusSpecification: z.string().optional(),
      marketExperience: z.string().min(1, { message: this.fieldRequired() }),
    }

    let zObject = z.object(rules).superRefine((data, ctx) => {
      if (data.type === 'COMPANY') {
        if (!data.company) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['company'],
            message: this.fieldRequired(),
          })
        }
        if (!data.companyDomainURL) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['companyDomainURL'],
            message: this.fieldRequired(),
          })
        }
      }

      if (data.mainFocus === 'Other (Please specify below)') {
        if (!data.mainFocusSpecification) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mainFocusSpecification'],
            message: this.fieldRequired(),
          })
        }
      }
    })

    return withZod(zObject)
  }

  public static affiliateAudienceInsightsStep() {
    let rules = {
      audienceSize: z.string().min(1, { message: this.fieldRequired() }),
      audienceReachingPlatforms: z
        .union([z.string(), z.array(z.string())])
        .transform((val) => (Array.isArray(val) ? val : [val]))
        .refine((val) => val.length > 0, { message: this.fieldRequired() }),
      audienceReachingPlatformsSpecification: z.string().optional(),
      targetingRegions: z
        .union([z.string(), z.array(z.string())])
        .transform((val) => (Array.isArray(val) ? val : [val]))
        .refine((val) => val.length > 0, { message: this.fieldRequired() }),
      audiencePrimaryLanguages: z
        .union([z.string(), z.array(z.string())])
        .transform((val) => (Array.isArray(val) ? val : [val]))
        .refine((val) => val.length > 0, { message: this.fieldRequired() }),
      audiencePrimaryLanguagesSpecification: z.string().optional(),
    }

    let zObject = z.object(rules).superRefine((data, ctx) => {
      if (data.audienceReachingPlatforms?.includes('Other (Please specify below)')) {
        if (!data.audienceReachingPlatformsSpecification) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['audienceReachingPlatformsSpecification'],
            message: this.fieldRequired(),
          })
        }
      }

      if (data.audiencePrimaryLanguages?.includes('Other (Please specify below)')) {
        if (!data.audiencePrimaryLanguagesSpecification) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['audiencePrimaryLanguagesSpecification'],
            message: this.fieldRequired(),
          })
        }
      }
    })

    return withZod(zObject)
  }

  public static affiliatePartnershipGoalsStep() {
    let rules = {
      promotionContents: z
        .union([z.string(), z.array(z.string())])
        .transform((val) => (Array.isArray(val) ? val : [val]))
        .refine((val) => val.length > 0, { message: this.fieldRequired() }),
      promotionContentsSpecification: z.string().optional(),
      affiliateMotivations: z
        .union([z.string(), z.array(z.string())])
        .transform((val) => (Array.isArray(val) ? val : [val]))
        .refine((val) => val.length > 0, { message: this.fieldRequired() }),
      affiliateMotivationsSpecification: z.string().optional(),
    }

    let zObject = z.object(rules).superRefine((data, ctx) => {
      if (data.promotionContents?.includes('Other (Please specify below)')) {
        if (!data.promotionContentsSpecification) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['promotionContentsSpecification'],
            message: this.fieldRequired(),
          })
        }
      }

      if (data.affiliateMotivations?.includes('Other (Please specify below)')) {
        if (!data.affiliateMotivationsSpecification) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['affiliateMotivationsSpecification'],
            message: this.fieldRequired(),
          })
        }
      }
    })

    return withZod(zObject)
  }

  public static affiliateAdditionalInfoStep(humanVerificationService: HumanVerificationService) {
    let rules = {
      otherAffiliateProgramsExperience: z.string().min(1, { message: this.fieldRequired() }),
      resourcesOrSupport: z.string().min(1, { message: this.fieldRequired() }),
      resourcesOrSupportSpecification: z.string().optional(),
      otherComments: z.string().optional(),
      tcAcknowledgement: z.string({ required_error: this.fieldRequired() }),
      recaptcha: z.string().min(1, { message: this.recaptchaRequired() }),
      turnstile: z.string().min(1, { message: this.turnstileRequired() }),
    }

    if (humanVerificationService !== 'GOOGLE_RECAPTCHA') delete (rules as any).recaptcha
    if (humanVerificationService !== 'CLOUDFLARE_TURNSTILE') delete (rules as any).turnstile

    let zObject = z.object(rules).superRefine((data, ctx) => {
      if (data.resourcesOrSupport === 'Yes (Please specify below)') {
        if (!data.resourcesOrSupportSpecification) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['resourcesOrSupportSpecification'],
            message: this.fieldRequired(),
          })
        }
      }
    })

    return withZod(zObject)
  }

  public static forgotPassword() {
    return withZod(
      z.object({
        email: z.string().min(1, { message: this.fieldRequired() }).email(this.validEmail()),
      }),
    )
  }

  public static submitPayoutOrder() {
    let rules: { [key: string]: any } = {
      orderId: z.string().min(1, { message: this.fieldRequired() }),
      paymentAccountId: z.string().min(1, { message: this.fieldRequired() }),
    }

    return withZod(z.object(rules))
  }

  public static signalProviderContract() {
    let rules: { [key: string]: any } = {
      documentId: z.string().min(1, { message: this.fieldRequired() }),
      acknowledgeSpa: z.string({ required_error: this.fieldRequired() }),
    }

    return withZod(z.object(rules))
  }

  public static resetExtend(includePayment = true) {
    let rules: { [key: string]: any } = {
      orderId: z.string().optional(),
      scalingPlanLevelId: z.string().min(1, { message: this.fieldRequired() }),
      offerId: z.string().min(1, { message: this.fieldRequired() }),
      accountId: z.string().min(1, { message: this.fieldRequired() }),
      paymentType: z.string().min(1, { message: this.fieldRequired() }),
      paymentMethod: z.string().optional(),
      scalingPlanId: z.string().min(1, { message: this.fieldRequired() }),
      voucherCode: z.string().optional(),
      currency: z.string().optional(),
      companyBankAccountId: z.string().optional(),
    }

    if (!includePayment) {
      delete rules['paymentType']
      delete rules['paymentMethod']
      delete rules['currency']
      delete rules['companyBankAccountId']
    }

    let zObject = z.object(rules).superRefine((data, ctx) => {
      if (data.paymentType === 'CR2FIAT' && !data.currency) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['currency'],
          message: this.fieldRequired(),
        })
      }

      if (data.paymentType === 'TFR' && !data.companyBankAccountId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['companyBankAccountId'],
          message: this.fieldRequired(),
        })
      }
    })

    return withZod(zObject)
  }

  public static verificationInfoStep(fields: string[]) {
    const dateString = z.preprocess((arg) => {
      if (arg instanceof Date) return arg

      if (typeof arg === 'string' && arg.length > 0) {
        const [day, month, year] = arg.split('/')
        //convert string to YYYY-MM-DD format, to get only date without time
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

        return new Date(dateStr)
      }

      return undefined
    }, z.date().optional())

    const expirationDateString = z.preprocess((arg) => {
      if (arg instanceof Date) return arg

      if (typeof arg === 'string' && arg.length > 0) {
        const [day, month, year] = arg.split('/')
        //convert string to YYYY-MM-DD format, to get only date without time
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

        return new Date(dateStr)
      }

      return undefined
    }, z.date().optional())
    let rules: { [key: string]: any } = {
      stepName: z.enum(Object.values(VerificationStepsEnum)),
      action: z.enum(['save', 'next-step', 'submit']),
      title: z.string().min(1, { message: this.fieldRequired() }),
      firstName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly()),
      lastName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly()),
      nationality: z.string().optional(),
      dateOfBirth: dateString,
      address: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsNumbersCommasRegEx, this.fieldLatinNumbersCommasRegEx())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      postCode: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      country: z.string({ required_error: this.fieldRequired() }),
      city: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      state: z.string().regex(this.LatinsCharsRegEx, this.fieldLatinOnly()).optional(),
      identificationNumber: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      expirationDate: expirationDateString,
      isSoleProprietor: z.string().optional(),
      soleProprietorVatNo: z
        .string()
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .optional(),
      soleProprietorTermsAccept: z.string().optional(),
      identificationDocumentNotExpires: z.string().optional(),
      residenceProofDocumentUploaded: z.string(),
      identityProofDocumentUploaded: z.string(),
      soleProprietorTaxFormUploaded: z.string(),
      soleProprietorRegistrationUploaded: z.string(),
      client_identityDocuments: z.string(),
      client_residenceProofFiles: z.string(),
      client_spTaxDocuments: z.string(),
      client_spRegistrationDocuments: z.string(),
    }

    if (fields.length > 0) {
      Object.keys(rules).forEach((key) => {
        if (!fields.includes(key)) delete rules[key]
      })
    }

    const schema = z.object(rules).superRefine((data, ctx) => {
      if (data.isSoleProprietor === 'on') {
        if (!data.soleProprietorVatNo || data.soleProprietorVatNo.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['soleProprietorVatNo'],
            message: this.fieldRequired(),
          })
        }

        if (!data.soleProprietorTermsAccept || data.soleProprietorTermsAccept === 'false') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['soleProprietorTermsAccept'],
            message: this.fieldRequired(),
          })
        }
      }

      if (data.dateOfBirth && this.calculateAge(data.dateOfBirth) < 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dateOfBirth'],
          message: this.datefieldGreaterThan18(),
        })
      }

      // for Step 1 - Personal Info
      // TODO: Is this redundant?
      if ('identificationNumber' in data) {
        const accountVerificationRequiredFields = [
          'dateOfBirth',
          // 'state',
          'city',
          'postCode',
          'address',
          'nationality',
          'identificationNumber',
        ]

        accountVerificationRequiredFields.forEach((field: string) => {
          if (data[field] === undefined || data[field]?.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [field],
              message: this.fieldRequired(),
            })
          }
        })

        // Require state only for US and CA
        if (['US', 'CA'].includes(data.country) && (!data.state || data.state.trim().length === 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['state'],
            message: this.fieldRequired(),
          })
        }

        if (data.identificationDocumentNotExpires !== 'true' && !data.expirationDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['expirationDate'],
            message: this.fieldRequired(),
          })
        }
      }

      // Conditional validation for post code for usa states
      const usaPostCodes = usaZipCodesByState

      if (usaPostCodes[data.state] !== undefined && !usaPostCodes[data.state].includes(data.postCode)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['postCode'], message: this.invalidPostCode() })
      }

      // Check if identityProofDocumentUploaded exists before validating
      if ('identityProofDocumentUploaded' in data && !data.identityProofDocumentUploaded) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['identityProofDocument'],
          message: this.noFileUploaded(),
        })
      }

      // Check if residenceProofDocumentUploaded exists before validating
      if ('residenceProofDocumentUploaded' in data && !data.residenceProofDocumentUploaded) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['residenceProofDocument'],
          message: this.noFileUploaded(),
        })
      }

      if ('soleProprietorTaxFormUploaded' in data && !data.soleProprietorTaxFormUploaded) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['soleProprietorTaxForm'],
          message: this.noFileUploaded(),
        })
      }

      if ('soleProprietorRegistrationUploaded' in data && !data.soleProprietorRegistrationUploaded) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['soleProprietorRegistration'],
          message: this.noFileUploaded(),
        })
      }
    })

    return withZod(schema)
  }

  // TODO: Should remove all fields that not applicable any more (the ones covered from verification validation)
  // TODO: Should rename this
  public static profile(fields?: string[]) {
    // fields param vary based on selected tab.

    const dateString = z.preprocess((arg) => {
      if (arg instanceof Date) return arg

      if (typeof arg === 'string' && arg.length > 0) {
        const [day, month, year] = arg.split('/')
        //convert string to YYYY-MM-DD format, to get only date without time
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

        return new Date(dateStr)
      }

      return undefined
    }, z.date().optional())

    const expirationDateString = z.preprocess((arg) => {
      if (arg instanceof Date) return arg

      if (typeof arg === 'string' && arg.length > 0) {
        const [day, month, year] = arg.split('/')
        //convert string to YYYY-MM-DD format, to get only date without time
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

        return new Date(dateStr)
      }

      return undefined
    }, z.date().optional())

    const incorporationDateString = z.preprocess((arg) => {
      if (arg instanceof Date) return arg

      if (typeof arg === 'string' && arg.length > 0) {
        // do not convert if already in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(arg)) return new Date(arg)

        const [day, month, year] = arg.split('/')
        //convert string to YYYY-MM-DD format, to get only date without time
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

        return new Date(dateStr)
      }

      return undefined
    }, z.date().optional())

    let rules: { [key: string]: any } = {
      title: z.string().min(1, { message: this.fieldRequired() }),
      firstName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly()),
      lastName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly()),
      nickname: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      dateOfBirth: dateString,
      phoneNumber: z.string().refine(isValidPhoneNumber, this.invalidPhone()),
      communicationLanguage: z.string({ required_error: this.fieldRequired() }),
      country: z.string({ required_error: this.fieldRequired() }),
      state: z.string().regex(this.LatinsCharsRegEx, this.fieldLatinOnly()).optional(),
      city: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      postCode: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      address: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsNumbersCommasRegEx, this.fieldLatinNumbersCommasRegEx())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      residenceProofDocumentUploaded: z.string(),
      isSoleProprietor: z.string().optional(),
      soleProprietorVatNo: z
        .string()
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .optional(),
      soleProprietorTermsAccept: z.string().optional(),

      //Identification Details - - - - - - - - - - - - -  - - - - - - - - -
      nationality: z.string().optional(),
      identificationNumber: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      expirationDate: expirationDateString,
      identificationDocumentNotExpires: z.string().optional(),
      identityProofDocumentUploaded: z.string(),
      isKycSubmission: z.string().optional(),

      //Corporate Details - - - - - - - - - - - - -  - - - - - - - - -
      companyName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsNumbersSpecialCharsRegEx, this.fieldLatinNumbersSpecialCharsRegEx())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      registrationNumber: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      vatNumber: z.string().regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()).optional(),
      taxIdNumber: z.string().regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()).optional(),
      incorporationDate: incorporationDateString,
      companyAddress: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsNumbersCommasRegEx, this.fieldLatinNumbersCommasRegEx())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      companyCity: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      companyState: z.string().regex(this.LatinsCharsRegEx, this.fieldLatinOnly()).optional(),
      companyPostCode: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }),
      companyCountry: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly()),
    }

    if (fields) {
      Object.keys(rules).forEach((key) => {
        if (!fields.includes(key)) delete rules[key]
      })
    }

    let zObject = z.object(rules).superRefine((data, ctx) => {
      if (data.isSoleProprietor === 'on') {
        if (!data.soleProprietorVatNo || data.soleProprietorVatNo.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['soleProprietorVatNo'],
            message: this.fieldRequired(),
          })
        }

        if (!data.soleProprietorTermsAccept || data.soleProprietorTermsAccept === 'false') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['soleProprietorTermsAccept'],
            message: this.fieldRequired(),
          })
        }
      }

      if (data.dateOfBirth && this.calculateAge(data.dateOfBirth) < 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dateOfBirth'],
          message: this.datefieldGreaterThan18(),
        })
      }

      if (data.isKycSubmission === 'true') {
        const accountVerificationRequiredFields = [
          'dateOfBirth',
          // 'country', //disabled issue after package upgrade
          'state',
          'city',
          'postCode',
          'address',
          'nationality',
          'identificationNumber',
        ]

        accountVerificationRequiredFields.forEach((field: string) => {
          if (data[field] === undefined || data[field]?.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [field],
              message: this.fieldRequired(),
            })
          }
        })

        if (!data.residenceProofDocumentUploaded) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['residenceProofDocument'],
            message: this.noFileUploaded(),
          })
        }

        if (!data.identityProofDocumentUploaded) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['identityProofDocument'],
            message: this.noFileUploaded(),
          })
        }

        if (data.identificationDocumentNotExpires !== 'true' && !data.expirationDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['expirationDate'],
            message: this.fieldRequired(),
          })
        }
      }

      // Conditional validation for post code for usa states
      const usaPostCodes = usaZipCodesByState

      if (usaPostCodes[data.state] !== undefined && !usaPostCodes[data.state].includes(data.postCode)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['postCode'], message: this.invalidPostCode() })
      }

      if (
        usaPostCodes[data.companyState] !== undefined &&
        !usaPostCodes[data.companyState].includes(data.companyPostCode)
      ) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['companyPostCode'], message: this.invalidPostCode() })
      }
    })

    return withZod(zObject)
  }

  // this is for Missing client details modal
  public static missingFieldsUponPurchase(fields?: string[]) {
    const dobString = z.preprocess(
      (arg) => {
        if (arg instanceof Date) return arg

        if (typeof arg === 'string' && arg.length > 0) {
          const [day, month, year] = arg.split('/')
          //convert string to YYYY-MM-DD format, to get only date without time
          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

          return new Date(dateStr)
        }

        return undefined
      },
      z.date({ required_error: this.fieldRequired() }).refine(
        (value) => {
          if (!value || !(value instanceof Date)) {
            return false
          }

          const age = this.calculateAge(value)

          return age >= 18
        },
        { message: this.datefieldGreaterThan18() },
      ),
    )

    let rules: { [key: string]: any } = {
      title: z.string().min(1, { message: this.fieldRequired() }),
      firstName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }), // Rejects empty or whitespace strings
      lastName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }), // Rejects empty or whitespace strings

      dateOfBirth: dobString,
      phoneNumber: z.string().refine(isValidPhoneNumber, this.invalidPhone()),
      country: z.string({ required_error: this.fieldRequired() }),
      state: z.string().regex(this.LatinsCharsRegEx, this.fieldLatinOnly()).optional(),
      city: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }), // Rejects empty or whitespace strings
      postCode: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }), // Rejects empty or whitespace strings
      address: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsNumbersCommasRegEx, this.fieldLatinNumbersCommasRegEx())
        .refine((val) => val.trim().length > 0, { message: this.fieldRequired() }), // Rejects empty or whitespace strings
    }

    if (fields) {
      Object.keys(rules).forEach((key) => {
        if (!fields.includes(key)) delete rules[key]
      })
    }

    // Conditional validation for post code for usa states
    let zObject = z.object(rules).superRefine((data, ctx) => {
      // Require state only for US and CA
      //  not working
      // if (['US', 'CA'].includes(data.country) && (!data.state || data.state.trim().length === 0)) {
      //   ctx.addIssue({
      //     code: z.ZodIssueCode.custom,
      //     path: ['state'],
      //     message: this.fieldRequired(),
      //   })
      // }

      const usaPostCodes = usaZipCodesByState

      if (usaPostCodes[data.state] !== undefined && !usaPostCodes[data.state].includes(data.postCode)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['postCode'], message: this.invalidPostCode() })
      }
    })

    return withZod(zObject)
  }

  public static marketDataPurchase() {
    let rules: { [key: string]: any } = {
      paymentType: z.string().min(1, { message: this.fieldRequired() }),
      paymentMethod: z.string().optional(),
      currency: z.string().optional(),
      companyBankAccountId: z.string().optional(),
      addons: z.string().optional(),
    }

    return withZod(z.object(rules))
  }

  public static checkout(includePayment = true, validateScalingPlanId = true) {
    let rules: { [key: string]: any } = {
      offerId: z.string({ required_error: this.fieldRequired() }),
      scalingPlanId: z.string({ required_error: this.fieldRequired() }),
      addons: z.string().optional(),
      // liveSquawkAddon: z.string().optional(),
      voucherCode: z.string().optional(),
      paymentType: z.string().min(1, { message: this.fieldRequired() }),
      paymentMethod: z.string().optional(),
      currency: z.string().optional(),
      marketDataPlatformId: z.string().optional(),
      companyBankAccountId: z.string().optional(),
    }

    if (!includePayment) {
      delete rules['paymentType']
      delete rules['paymentMethod']
      delete rules['currency']
      delete rules['companyBankAccountId']
    }

    if (!validateScalingPlanId) delete rules['scalingPlanId']

    let zObject = z.object(rules).superRefine((data, ctx) => {
      if (data.paymentType === 'CR2FIAT' && !data.currency) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['currency'],
          message: this.fieldRequired(),
        })
      }

      if (data.paymentType === 'TFR' && !data.companyBankAccountId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['companyBankAccountId'],
          message: this.fieldRequired(),
        })
      }
    })

    return withZod(zObject)
  }

  public static activationFee() {
    let rules: { [key: string]: any } = {
      contractId: z.string({ required_error: this.fieldRequired() }),
      requestId: z.string({ required_error: this.fieldRequired() }),
      paymentType: z.string().min(1, { message: this.fieldRequired() }),
      paymentMethod: z.string().optional(),
      currency: z.string().optional(),
      companyBankAccountId: z.string().optional(),
    }

    let zObject = z.object(rules).superRefine((data, ctx) => {
      if (data.paymentType === 'CR2FIAT' && !data.currency) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['currency'],
          message: this.fieldRequired(),
        })
      }

      if (data.paymentType === 'TFR' && !data.companyBankAccountId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['companyBankAccountId'],
          message: this.fieldRequired(),
        })
      }
    })

    return withZod(zObject)
  }

  // I use this when I want to ignore client-side validation, and log server side validation result
  // public static test() {
  //   let rules: { [key: string]: any } = {}

  //   return withZod(z.object(rules))
  // }

  // Bank Account
  public static bankAccount() {
    let rules: { [key: string]: any } = {
      beneficiaryFullName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly()),
      currencyId: z.string().min(1, { message: this.fieldRequired() }),
      beneficiaryAddress: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsNumbersCommasRegEx, this.fieldLatinNumbersCommasRegEx()),
      beneficiaryCity: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly()),
      beneficiaryPostalCode: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()),
      beneficiaryCountry: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly()),
      accountNumber: z.string().regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()).optional(),
      iban: z.preprocess(
        (value) => (value && typeof value === 'string' ? value.replace(/\s/g, '') : undefined),
        z.string().regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()).optional(),
      ),
      bankName: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()),
      branchName: z.string().regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()).optional(),
      bankAddress: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsNumbersCommasRegEx, this.fieldLatinNumbersCommasRegEx()),
      bankCountry: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsRegEx, this.fieldLatinOnly()),
      sepaEnabled: z.string().optional(),
      swiftBic: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()),
      routingNumber: z.string().regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()).optional(),
      descriptionForPayment: z
        .string()
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly())
        .optional(),
      bankAccountDocumentUploaded: z.string().optional(),
      // bankAccountPreferred: z.string().optional(),
    }

    // Conditional validation for post code for usa states
    let zObject = z.object(rules).superRefine((data, ctx) => {
      if (!data.bankAccountDocumentUploaded) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bankAccountDocument'],
          message: this.noFileUploaded(),
        })
      }

      if (data.iban?.length > 0) {
        try {
          const bban = IBAN.toBBAN(data.iban, '')
          if (!IBAN.isValidBBAN(data.bankCountry, bban)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['iban'],
              message: 'Invalid IBAN',
            })
          }
        } catch (e) {
          console.log('error', e)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['iban'],
            message: 'Invalid IBAN',
          })
        }
      }

      if (data.sepaEnabled && data.sepaEnabled === 'true' && !data.iban) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['iban'], message: this.fieldRequired() })
      }

      if (data.sepaEnabled && data.sepaEnabled === 'false' && !data.accountNumber) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['accountNumber'], message: this.fieldRequired() })
      }
    })

    return withZod(zObject)
  }

  public static cryptoAccount() {
    let rules: { [key: string]: any } = {
      cryptoCurrencyId: z.string().min(1, { message: this.fieldRequired() }),
      description: z.string().regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()).optional(),
      cryptoAddress: z
        .string()
        .min(1, { message: this.fieldRequired() })
        .regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()),
      destinationTag: z.string().regex(this.LatinsCharsAndNumbersRegEx, this.fieldLatinAndNumbersOnly()).optional(),
      // cryptoAddressPreferred: z.string().optional(),
    }

    return withZod(z.object(rules))
  }

  public static archivePaymentAccount() {
    let rules: { [key: string]: any } = {
      paymentAccountId: z.string().min(1, { message: this.fieldRequired() }),
    }

    return withZod(z.object(rules))
  }

  public static upgradeRequest() {
    let rules: { [key: string]: any } = {
      technologyProvider: z.string().min(1, { message: this.fieldRequired() }),
      tradingPlatformAccountId: z.string().min(1, { message: this.fieldRequired() }),
      scalingPlanLevelId: z.string().min(1, { message: this.fieldRequired() }),
      clientId: z.string().min(1, { message: this.fieldRequired() }),
      contractId: z.string().min(1, { message: this.fieldRequired() }),
      upgradeRequestType: z.string().min(1, { message: this.fieldRequired() }),
    }

    return withZod(z.object(rules))
  }

  public static payoutRequest(isAnytimePayout = false) {
    let rules: { [key: string]: any } = {
      technologyProvider: z.string().min(1, { message: this.fieldRequired() }),
      tradingPlatformAccountId: z.string().min(1, { message: this.fieldRequired() }),
      scalingPlanLevelId: z.string().min(1, { message: this.fieldRequired() }),
      clientId: z.string().min(1, { message: this.fieldRequired() }),
      contractId: z.string().min(1, { message: this.fieldRequired() }),
      upgradeRequestType: z.string().min(1, { message: this.fieldRequired() }),
      payoutPaymentAccountId: z.string().min(1, { message: this.fieldRequired() }),
      requestedWithdrawalAmount: z.string().min(1, { message: this.fieldRequired() }),
    }

    if (!isAnytimePayout) {
      delete rules['requestedWithdrawalAmount']
    }

    return withZod(z.object(rules))
  }
}

export default Validation
